import { useEffect, useState } from "react";
import { Button, Card, Form, Input, Modal, Select, Switch, Table, Tag, Typography, message } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { PageHeader } from "../../components/PageHeader";
import { SiteLabel } from "../../components/SiteLabel";
import { SiteSelect } from "../../components/SiteSelect";
import { api } from "../../helpers/api";
import { useApi } from "../../helpers/useApi";
import { useProjectsQuery, useSitesQuery } from "../../helpers/queries";
import type { Paginated, PendingRoleAssignment, Role, User, UserRoleAssignment } from "../../api-models";

interface UserRow extends User {
  assignments: UserRoleAssignment[];
}

export default function UsersPage() {
  const { request } = useApi();
  const { data: sites = [] } = useSitesQuery();
  const { data: projects = [] } = useProjectsQuery();
  const [rows, setRows] = useState<UserRow[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [pending, setPending] = useState<PendingRoleAssignment[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [assignFor, setAssignFor] = useState<UserRow | null>(null);
  const [assignForm] = Form.useForm();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm] = Form.useForm();

  async function load() {
    const [u, r, p] = await Promise.all([
      api.get<Paginated<UserRow>>("/users", { params: { pageSize: 200 } }),
      api.get<Role[]>("/roles"),
      api.get<PendingRoleAssignment[]>("/pending-assignments"),
    ]);
    setRows(u.data.data);
    setRoles(r.data);
    setPending(p.data);
  }

  useEffect(() => {
    load();
  }, []);

  async function createUser() {
    const values = await createForm.validateFields();
    await request({ method: "POST", url: "/users", data: values });
    message.success("User created.");
    setCreateOpen(false);
    load();
  }

  async function toggleActive(user: UserRow, active: boolean) {
    await request({ method: "PUT", url: `/users/${user.id}`, data: { name: user.name, active } });
    load();
  }

  async function addAssignment() {
    if (!assignFor) return;
    const values = await assignForm.validateFields();
    await request({ method: "POST", url: `/users/${assignFor.id}/assignments`, data: values });
    message.success("Role assigned.");
    setAssignFor(null);
    load();
  }

  async function removeAssignment(user: UserRow, assignmentId: number) {
    await request({ method: "DELETE", url: `/users/${user.id}/assignments/${assignmentId}` });
    load();
  }

  async function addPending() {
    const values = await inviteForm.validateFields();
    await request({ method: "POST", url: "/pending-assignments", data: values });
    message.success("Role staked out — it applies the first time they sign in.");
    setInviteOpen(false);
    load();
  }

  async function removePending(id: number) {
    await request({ method: "DELETE", url: `/pending-assignments/${id}` });
    load();
  }

  const watchedRoleId = Form.useWatch("roleId", assignForm);
  const selectedRole = roles.find((r) => r.id === watchedRoleId);
  const watchedInviteRoleId = Form.useWatch("roleId", inviteForm);
  const selectedInviteRole = roles.find((r) => r.id === watchedInviteRoleId);

  return (
    <div>
      <PageHeader
        title="Users"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createForm.resetFields();
              setCreateOpen(true);
            }}
          >
            New user
          </Button>
        }
      />
      <Table
        rowKey="id"
        dataSource={rows}
        columns={[
          { title: "Name", dataIndex: "name" },
          { title: "Email", dataIndex: "email" },
          { title: "Provider", dataIndex: "provider" },
          {
            title: "Roles",
            render: (_, r) => (
              <>
                {r.assignments.map((a) => (
                  <Tag key={a.id} closable onClose={() => removeAssignment(r, a.id)}>
                    {a.role?.name}
                    {a.site ? <> @ <SiteLabel site={a.site} /></> : ""}
                    {a.project ? ` @ ${a.project.name}` : ""}
                  </Tag>
                ))}
                <Button
                  size="small"
                  type="dashed"
                  onClick={() => {
                    assignForm.resetFields();
                    setAssignFor(r);
                  }}
                >
                  + Assign
                </Button>
              </>
            ),
          },
          { title: "Active", dataIndex: "active", render: (v, r) => <Switch checked={v} onChange={(checked) => toggleActive(r, checked)} /> },
        ]}
      />
      <Card
        title="Pending role assignments"
        style={{ marginTop: 24 }}
        extra={
          <Button
            icon={<PlusOutlined />}
            data-tour="users-invite"
            onClick={() => {
              inviteForm.resetFields();
              setInviteOpen(true);
            }}
          >
            Stake out a role
          </Button>
        }
      >
        <Typography.Paragraph type="secondary">
          Roles held for an email address that has no account yet. The role is applied automatically the first time that address signs in — which is
          also what lets them in, so nothing else needs to be created for them up front.
        </Typography.Paragraph>
        <Table
          rowKey="id"
          dataSource={pending}
          pagination={false}
          locale={{ emptyText: "No roles are waiting on a first sign-in." }}
          columns={[
            { title: "Email", dataIndex: "email" },
            { title: "Name", dataIndex: "name", render: (v: string) => v || "—" },
            {
              title: "Role",
              render: (_, p) => (
                <Tag>
                  {p.role?.name}
                  {p.site ? <> @ <SiteLabel site={p.site} /></> : ""}
                  {p.project ? ` @ ${p.project.name}` : ""}
                </Tag>
              ),
            },
            { title: "Added by", render: (_, p) => p.invitedBy?.name ?? "—" },
            { title: "", width: 48, render: (_, p) => <Button icon={<DeleteOutlined />} danger type="text" onClick={() => removePending(p.id)} /> },
          ]}
        />
      </Card>
      <Modal title="New user" open={createOpen} onCancel={() => setCreateOpen(false)} onOk={createUser} destroyOnHidden>
        <Typography.Paragraph type="secondary">
          Users must exist here before they can sign in — no self-registration. To onboard someone without knowing their name or provider yet, stake
          out a role for their email below instead.
        </Typography.Paragraph>
        <Form form={createForm} layout="vertical">
          <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="provider" label="Identity provider" rules={[{ required: true }]}>
            <Select options={[{ value: "google", label: "Google" }, { value: "microsoft", label: "Microsoft" }]} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal title={`Assign role to ${assignFor?.name}`} open={!!assignFor} onCancel={() => setAssignFor(null)} onOk={addAssignment} destroyOnHidden>
        <Form form={assignForm} layout="vertical">
          <Form.Item name="roleId" label="Role" rules={[{ required: true }]}>
            <Select options={roles.map((r) => ({ value: r.id, label: r.name }))} />
          </Form.Item>
          {selectedRole?.scopeType === "SITE" && (
            <Form.Item name="siteId" label="Site" rules={[{ required: true }]}>
              <SiteSelect options={sites.map((s) => ({ value: s.id, label: s.name }))} />
            </Form.Item>
          )}
          {selectedRole?.scopeType === "PROJECT" && (
            <Form.Item
              name="projectId"
              label="Project"
              rules={[{ required: true }]}
              extra="Covers every site of this project, including sites added later."
            >
              <Select options={projects.map((p) => ({ value: p.id, label: p.name }))} />
            </Form.Item>
          )}
        </Form>
      </Modal>
      <Modal title="Stake out a role for an email" open={inviteOpen} onCancel={() => setInviteOpen(false)} onOk={addPending} destroyOnHidden>
        <Typography.Paragraph type="secondary">
          For someone who has not signed in yet. Their account is created with this role the first time they sign in with this address.
        </Typography.Paragraph>
        <Form form={inviteForm} layout="vertical">
          <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name" extra="Optional — used only if their provider does not supply one.">
            <Input />
          </Form.Item>
          <Form.Item name="roleId" label="Role" rules={[{ required: true }]}>
            <Select options={roles.map((r) => ({ value: r.id, label: r.name }))} />
          </Form.Item>
          {selectedInviteRole?.scopeType === "SITE" && (
            <Form.Item name="siteId" label="Site" rules={[{ required: true }]}>
              <SiteSelect options={sites.map((s) => ({ value: s.id, label: s.name }))} />
            </Form.Item>
          )}
          {selectedInviteRole?.scopeType === "PROJECT" && (
            <Form.Item
              name="projectId"
              label="Project"
              rules={[{ required: true }]}
              extra="Covers every site of this project, including sites added later."
            >
              <Select options={projects.map((p) => ({ value: p.id, label: p.name }))} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
